import { useMemo, useState } from "react";
import { Trash2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  useDeleteSablona,
  useInsertSablona,
  useSablona,
  useSablony,
} from "@/api/hooks/useSablony";
import { useDebounce } from "@/lib/useDebounce";
import type { MaterialSlot } from "@/api/schemas";

/** Tvar snapshotu šablóny zostavy (JSON `data` z backendu). */
interface SnapshotMaterialRef {
  id: string;
  nazov: string;
}
interface SnapshotSlot {
  slot_kod: string;
  material: SnapshotMaterialRef | null;
}
interface SnapshotDielec {
  kod: string;
  nazov_strojovy: string | null;
  pocet_ks: number;
  slot_kod: string | null;
  x_mm: string | null;
  y_mm: string | null;
  z_mm: string | null;
}
interface SablonaSnapshot {
  zostava: { kod: string; nazov: string };
  sloty: SnapshotSlot[];
  dielce: SnapshotDielec[];
}

/**
 * Výber a vloženie šablóny do aktívnej varianty. Sloty sa default
 * mapujú podľa kódu (M1 → M1 cieľovej varianty — dielce sa adaptujú
 * na jej materiály); pri rozdielnom materiáli sa dá zvoliť nový slot.
 */
export function SablonaInsertDialog({
  zakazkaId,
  variantaId,
  sloty,
  open,
  onOpenChange,
}: {
  zakazkaId: string;
  variantaId: string;
  sloty: MaterialSlot[];
  open: boolean;
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const [search, setSearch] = useState("");
  const debounced = useDebounce(search, 250);
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [novySlotPre, setNovySlotPre] = useState<Set<string>>(new Set());

  const list = useSablony({ search: debounced, typ: "zostava" });
  const detail = useSablona(selectedId);
  const insert = useInsertSablona(zakazkaId);
  const remove = useDeleteSablona();

  const sablony = list.data?.data ?? [];
  const snapshot = detail.data?.data
    ? (detail.data.data.data as unknown as SablonaSnapshot)
    : undefined;

  const slotDiff = useMemo(() => {
    if (!snapshot) return [];
    const cielove = new Map(sloty.map((s) => [s.slot_kod, s]));
    return (snapshot.sloty ?? []).map((snap) => {
      const ciel = cielove.get(snap.slot_kod);
      return {
        slot_kod: snap.slot_kod,
        sablona_material: snap.material?.nazov ?? "—",
        ciel_material: ciel?.material?.nazov ?? null,
        existuje: Boolean(ciel),
        rozdielny:
          Boolean(ciel) &&
          Boolean(snap.material) &&
          ciel?.material_id !== snap.material?.id,
      };
    });
  }, [snapshot, sloty]);

  function reset(): void {
    setSelectedId(undefined);
    setNovySlotPre(new Set());
  }

  function toggleNovySlot(slotKod: string): void {
    setNovySlotPre((prev) => {
      const next = new Set(prev);
      if (next.has(slotKod)) next.delete(slotKod);
      else next.add(slotKod);
      return next;
    });
  }

  async function handleInsert(): Promise<void> {
    if (!selectedId) return;
    try {
      const slot_actions = Object.fromEntries(
        [...novySlotPre].map((k) => [k, "new" as const]),
      );
      const { data } = await insert.mutateAsync({
        sablonaId: selectedId,
        data: {
          varianta_id: variantaId,
          slot_actions: novySlotPre.size ? slot_actions : null,
        },
      });
      toast({
        title: `Zostava ${data.zostava.kod} vložená`,
        description: `${data.dielce_created} dielcov zo šablóny.`,
      });
      for (const w of data.warnings) {
        toast({ variant: "destructive", title: "Upozornenie", description: w.message });
      }
      reset();
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Šablónu sa nepodarilo vložiť",
        description: extractErrorMessage(err),
      });
    }
  }

  async function handleDelete(id: string, nazov: string): Promise<void> {
    if (!window.confirm(`Zmazať šablónu „${nazov}"?`)) return;
    try {
      await remove.mutateAsync(id);
      if (selectedId === id) reset();
      toast({ title: "Šablóna zmazaná", description: nazov });
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Vložiť šablónu</DialogTitle>
          <DialogDescription className="sr-only">
            Vloženie uloženej zostavy do tejto zákazky.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="sab-search" className="text-xs">
              Hľadať šablónu
            </Label>
            <Input
              id="sab-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="názov šablóny…"
              autoFocus
            />
          </div>
          <div className="max-h-40 overflow-y-auto rounded border">
            {sablony.length === 0 ? (
              <p className="p-3 text-xs text-muted-foreground">
                {list.isLoading
                  ? "Načítavam…"
                  : "Žiadne šablóny. Ulož zostavu cez ikonu záložky v tabuľke."}
              </p>
            ) : (
              sablony.map((s) => (
                <div
                  key={s.id}
                  className={`flex cursor-pointer items-center justify-between gap-2 border-b px-3 py-1.5 text-sm last:border-b-0 hover:bg-muted ${
                    selectedId === s.id ? "bg-muted" : ""
                  }`}
                  onClick={() => setSelectedId(s.id)}
                >
                  <div className="min-w-0">
                    <span className="font-medium">{s.nazov}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {s.pocet_dielcov} dielcov
                      {s.popis ? ` · ${s.popis}` : ""}
                    </span>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 shrink-0 hover:text-destructive"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDelete(s.id, s.nazov);
                    }}
                    aria-label={`Zmazať šablónu ${s.nazov}`}
                    title="Zmazať šablónu"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))
            )}
          </div>

          {snapshot ? (
            <>
              <div className="rounded border bg-muted/40 p-2">
                <p className="mb-1 text-xs font-semibold">
                  Náhľad: {snapshot.zostava.nazov} (
                  {snapshot.dielce.length} dielcov)
                </p>
                <div className="max-h-32 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {snapshot.dielce.map((d) => (
                        <tr key={d.kod} className="border-t first:border-t-0">
                          <td className="py-0.5 pr-2 font-mono">{d.kod}</td>
                          <td className="py-0.5 pr-2">
                            {d.nazov_strojovy ?? "—"}
                          </td>
                          <td className="py-0.5 pr-2 text-right font-mono">
                            {d.x_mm && d.y_mm
                              ? `${parseFloat(d.x_mm)}×${parseFloat(d.y_mm)}`
                              : ""}
                          </td>
                          <td className="py-0.5 text-right font-mono">
                            {d.pocet_ks} ks
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              {slotDiff.length ? (
                <div className="space-y-1">
                  <p className="text-xs font-semibold">Materiály</p>
                  {slotDiff.map((s) => (
                    <div
                      key={s.slot_kod}
                      className="flex items-center justify-between gap-2 text-xs"
                    >
                      <span>
                        <b>{s.slot_kod}</b>{" "}
                        {!s.existuje ? (
                          <span className="text-muted-foreground">
                            vytvorí sa ({s.sablona_material})
                          </span>
                        ) : s.rozdielny ? (
                          <span className="text-amber-700 dark:text-amber-400">
                            vo variante: {s.ciel_material} — dielce sa
                            adaptujú
                          </span>
                        ) : (
                          <span className="text-muted-foreground">
                            rovnaký materiál
                          </span>
                        )}
                      </span>
                      {s.existuje && s.rozdielny ? (
                        <label className="flex shrink-0 items-center gap-1">
                          <input
                            type="checkbox"
                            checked={novySlotPre.has(s.slot_kod)}
                            onChange={() => toggleNovySlot(s.slot_kod)}
                          />
                          nový materiál zo šablóny
                        </label>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            title="Zavrieť bez vloženia"
          >
            Zrušiť
          </Button>
          <Button
            onClick={() => void handleInsert()}
            disabled={!selectedId || insert.isPending}
            title="Vloží šablónu ako novú zostavu do aktívneho výpočtu"
          >
            {insert.isPending ? "Vkladám…" : "Vložiť"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
